import { json, redirect } from '@remix-run/node';
import React, { useEffect } from 'react';
import PreviewTabs from '~/components/preview/preview-tabs';
import { fetchDistributionPreview } from '~/distributionapi/api.server';
import type { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/node';
import type { DeviceType } from '~/components/preview/config';
import type { PreviewFetchError } from '~/distributionapi/api.server';
import '~/styles/newspaperpreview.css';
import { Alert, TextField } from '@fabric-ds/react';
import { Form, useActionData, useLoaderData, useNavigation } from '@remix-run/react';
import { fetchFacebookPreviews } from '~/facebook/facebook.server';
import { trackBlinkPreviewRequest, trackBlinkPreviewVisited } from '~/utils/tracking';

type LoaderError = 'invalid_ad_id' | PreviewFetchError;

type PreviewConfig = {
    finnkode: number;
    layout: 'tabs';
    baseUrl: string;
    wwwUrl: string;
    creativeId: number;
    version: string;
    facebook: any;
    deviceType: DeviceType;
    ad: any;
    channelsFromClient: boolean;
};

const validatedAdId = (adId: string): number | null => {
    const parsedId = Number(adId);
    if (isNaN(parsedId) || !Number.isInteger(parsedId)) {
        return null;
    }

    return parsedId;
};

export async function action({ request }: ActionFunctionArgs) {
    const formData = await request.formData();
    const finncode = formData.get('finncode');

    const validAdId = validatedAdId(finncode as string);

    if (validAdId === null) {
        return { error: 'invalid_ad_id' };
    }

    return redirect(`/blink/preview?finncode=${validAdId}`);
}

export async function loader({ request, context }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    const urlAdId = url.searchParams.get('finnkode') || url.searchParams.get('finncode');

    if (urlAdId === null || urlAdId === undefined) {
        return json({
            success: true,
            previewConfig: null,
            error: null,
        });
    }

    const validAdId = validatedAdId(urlAdId);

    if (validAdId !== null) {
        const previewFetchResult = await fetchDistributionPreview(validAdId);

        if (!previewFetchResult.success) {
            return json({
                success: false,
                error: previewFetchResult.reason,
                previewConfig: null,
            });
        }

        const { mountOrigin, publicPathname, deviceType } = context;
        let url = '';
        if (publicPathname && mountOrigin) {
            url = new URL(publicPathname, mountOrigin).toString();
        }

        const fbPreview = await fetchFacebookPreviews(validAdId);

        const previewConfig: PreviewConfig = {
            finnkode: validAdId,
            layout: 'tabs',
            baseUrl: url,
            wwwUrl: 'https://www.finn.no',
            creativeId: -1,
            version: 'eik',
            facebook: fbPreview,
            deviceType: deviceType as DeviceType,
            ad: previewFetchResult.data.ad,
            channelsFromClient: false,
        };

        return json({
            success: true,
            previewConfig,
            error: null,
        });
    } else {
        return json({
            success: false,
            error: 'invalid_ad_id',
            previewConfig: null,
        });
    }
}

export default function Index() {
    const data = useLoaderData<typeof loader>();
    const actionData = useActionData<typeof action>();

    const navigation = useNavigation();

    useEffect(() => {
        trackBlinkPreviewVisited();
    }, []);

    return (
        <div className="page-container mb-32">
            <h1>Forhåndsvisning av annonseformater</h1>
            <Form className="flex flex-grow items-center" method={'post'}>
                <TextField type="text" name="finncode" className="flex-1" />
                <button
                    className="button button--secondary flex-1 ml-12"
                    disabled={navigation.state !== 'idle'}
                    onClick={() => {
                        trackBlinkPreviewRequest();
                    }}
                    type="submit">
                    Vis annonsen
                </button>
                {actionData?.error && <div>{renderError(actionData.error as LoaderError)}</div>}
            </Form>

            {navigation.state === 'loading' && <div className="mt-16">Laster...</div>}

            {navigation.state === 'idle' &&
                data.success &&
                (data.previewConfig ? (
                    <div className="mt-16">
                        <PreviewTabs config={data.previewConfig} />
                    </div>
                ) : null)}
            {!data.success && data.error !== null && <div className="mt-16">{renderError(data.error as LoaderError)}</div>}
        </div>
    );
}

const renderError = (error: LoaderError) => {
    const text = errorText(error);
    return (
        <Alert type={'warning'} role="warning" show>
            <strong className="font-bold">Oops! </strong>
            <span className="block sm:inline">{text}</span>
        </Alert>
    );
};

const errorText = (error: LoaderError) => {
    switch (error) {
        case 'invalid_ad_id':
            return 'Ugyldig finnkode.';
        case 'not_found':
            return 'Fant ikke annonsen.';
        case 'unknown_ad_type':
            return 'Forhåndsvisning av denne annonsetypen er ikke støttet.';
        case 'error':
        case null:
        default:
            return 'Noe gikk galt.';
    }
};
